import { motion } from "framer-motion";
import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Star, Key, Sparkles, Camera, BarChart3, Shirt, ClipboardCheck, Wrench, ArrowRight, Check, Phone, FileText, Users, Shield, Award, ChevronRight, ChevronLeft } from "lucide-react";
import { AnimatedCounter } from "@/components/AnimatedCounter";
import { getPublicTemoignages } from "@/lib/api";

const HERO_IMAGE = "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&w=1920&q=80";

const services = [
  { icon: Key, title: "Accueil des voyageurs", desc: "Check-in/check-out personnalisé 7j/7, livret d'accueil et recommandations locales." },
  { icon: Sparkles, title: "Ménage inter-séjour", desc: "Nettoyage professionnel selon un protocole hôtelier en 45 points." },
  { icon: Shirt, title: "Gestion du linge", desc: "Linge de maison premium fourni, lavé et remplacé à chaque rotation." },
  { icon: ClipboardCheck, title: "États des lieux", desc: "Reportage photo avant/après chaque séjour pour protéger votre bien." },
  { icon: Camera, title: "Photos & annonces", desc: "Shooting professionnel et annonces optimisées sur toutes les plateformes." },
  { icon: BarChart3, title: "Tarification dynamique", desc: "Optimisation quotidienne des tarifs pour maximiser vos revenus." },
  { icon: Wrench, title: "Maintenance", desc: "Réseau d'artisans locaux pour interventions rapides et coordonnées." },
  { icon: Star, title: "Communication voyageurs", desc: "Gestion de toutes les interactions : réservations, questions, avis." },
];

const avantages = [
  "Vous gardez le contrôle de vos disponibilités",
  "Revenus potentiellement supérieurs au bail classique",
  "Commission transparente sur les réservations",
  "Rapports mensuels détaillés",
  "Gestion complète par notre équipe",
  "Visibilité multi-plateformes (Airbnb, Booking, etc.)",
  "Aucun investissement de départ",
  "Résiliation flexible",
];

const processSteps = [
  { num: "01", title: "Contact & Visite", desc: "Nous visitons votre bien et définissons ensemble la stratégie optimale." },
  { num: "02", title: "Mise en place", desc: "Shooting photo, création d'annonces, ameublement si nécessaire." },
  { num: "03", title: "Gestion quotidienne", desc: "Accueil, ménage, linge, maintenance — tout est pris en charge." },
];

const comparatif = [
  { feature: "Accueil des voyageurs", solo: "Vous-même", happyhouse: "Notre équipe 7j/7" },
  { feature: "Ménage inter-séjour", solo: "À organiser", happyhouse: "Protocole 45 points" },
  { feature: "Gestion du linge", solo: "À votre charge", happyhouse: "Linge premium inclus" },
  { feature: "Photos & annonces", solo: "À faire soi-même", happyhouse: "Shooting pro + SEO" },
  { feature: "Tarification", solo: "Prix fixe", happyhouse: "Tarification dynamique" },
  { feature: "Maintenance", solo: "À gérer", happyhouse: "Réseau artisans <24h" },
  { feature: "Communication", solo: "Messages 24/7", happyhouse: "Équipe dédiée" },
  { feature: "Rapports", solo: "Aucun", happyhouse: "Rapport mensuel détaillé" },
];

export default function ConciergeriePage() {
  const [temoignages, setTemoignages] = useState([]);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const autoplayRef = useRef(null);

  useEffect(() => {
    getPublicTemoignages("proprietaire").then(r => setTemoignages(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="conciergerie-page">
      {/* Hero */}
      <section className="relative min-h-[50vh] flex items-end overflow-hidden">
        <img src={HERO_IMAGE} alt="Conciergerie privée" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1a1008]/90 via-[#1a1008]/40 to-[#1a1008]/10" />
        <div className="relative w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 pt-32">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[#C9922A] uppercase tracking-[0.2em] text-sm font-semibold mb-3">
            Offre Partenariat
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="font-display text-3xl sm:text-4xl lg:text-5xl text-white leading-tight max-w-3xl">
            Conciergerie privée
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="text-white/60 mt-4 max-w-2xl text-lg">
            Une gestion haut de gamme de vos locations courte durée. Vous gardez le contrôle, nous nous occupons de tout le reste.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }} className="mt-8 flex flex-col sm:flex-row gap-4">
            <Link to="/proprietaires#contact-form" className="inline-flex items-center gap-2 px-7 py-3 bg-[#C9922A] text-white rounded-full font-semibold hover:bg-[#B8831F] transition-colors" data-testid="conciergerie-hero-cta">
              Devenir partenaire <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/simulateur" className="inline-flex items-center gap-2 px-7 py-3 border-2 border-white/30 text-white rounded-full font-semibold hover:bg-white/10 transition-colors">
              Estimer mes revenus
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Comment ça marche - 3 étapes */}
      <section className="py-20 bg-[#F5F0EB]" data-testid="conciergerie-process">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-[#C9922A] uppercase tracking-[0.2em] text-sm font-semibold mb-3 block">Comment ça marche</span>
            <h2 className="font-display text-3xl md:text-4xl text-[#004333]">3 étapes pour commencer</h2>
            <p className="text-[#6B5E50] mt-3 max-w-lg mx-auto">Un démarrage rapide et sans friction.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {processSteps.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12 }}
                className="relative bg-white rounded-2xl p-7 border border-[#E0D6CA] shadow-sm hover:shadow-md transition-shadow"
              >
                <span className="font-mono text-5xl font-black text-[#C9922A]/10 absolute top-4 right-4">{step.num}</span>
                <div className="w-10 h-10 rounded-xl bg-[#004333] flex items-center justify-center mb-5">
                  <span className="font-mono text-sm font-bold text-white">{step.num}</span>
                </div>
                <h3 className="font-semibold text-[#004333] text-lg mb-2">{step.title}</h3>
                <p className="text-[#6B5E50] text-sm leading-relaxed">{step.desc}</p>
                {i < processSteps.length - 1 && (
                  <ChevronRight className="hidden md:block absolute -right-5 top-1/2 -translate-y-1/2 w-5 h-5 text-[#C9922A]/40 z-10" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services inclus */}
      <section className="py-20 bg-[#EDE8E1]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-[#C9922A] uppercase tracking-[0.2em] text-sm font-semibold mb-3 block">Services inclus</span>
            <h2 className="font-display text-3xl md:text-4xl text-[#004333]">Une conciergerie complète</h2>
            <p className="text-[#6B5E50] mt-3 max-w-xl mx-auto">Chaque aspect de la gestion locative est pris en charge par notre équipe.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {services.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="bg-white rounded-2xl p-5 border border-[#E0D6CA] shadow-sm hover:shadow-md hover:border-[#C9922A]/30 transition-all group"
                data-testid={`conciergerie-service-${i}`}
              >
                <div className="w-11 h-11 rounded-xl bg-[#C9922A]/10 border border-[#C9922A]/20 flex items-center justify-center mb-4 group-hover:bg-[#C9922A]/15 transition-colors">
                  <item.icon className="w-5 h-5 text-[#C9922A]" />
                </div>
                <h3 className="font-semibold text-[#004333] mb-1.5">{item.title}</h3>
                <p className="text-[#6B5E50] text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tarification */}
      <section className="py-20 bg-[#F5F0EB]" data-testid="conciergerie-pricing">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-[#C9922A] uppercase tracking-[0.2em] text-sm font-semibold mb-3 block">Tarification</span>
          <h2 className="font-display text-3xl md:text-4xl text-[#004333] mb-4">Un modèle simple et transparent</h2>
          <p className="text-[#6B5E50] mb-10 max-w-2xl mx-auto">
            Pas de frais cachés, pas d'engagement excessif. Notre commission est prélevée uniquement sur les réservations effectuées.
          </p>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-white rounded-2xl border border-[#E0D6CA] shadow-sm p-8 md:p-12 max-w-lg mx-auto">
            <div className="mb-6">
              <span className="text-[#8B7D6B] text-sm">Commission à partir de</span>
              <div className="text-5xl font-black text-[#004333] mt-1">20<span className="text-3xl">%</span></div>
              <span className="text-[#8B7D6B] text-sm">du chiffre d'affaires des réservations</span>
            </div>
            <ul className="text-left space-y-3 mb-8">
              {["Toutes les prestations incluses", "Aucun coût fixe mensuel", "Pas de frais de mise en service", "Résiliation à tout moment"].map((item, i) => (
                <li key={i} className="flex items-center gap-2.5 text-[#6B5E50] text-sm">
                  <Check className="w-4 h-4 text-[#C9922A] flex-shrink-0" /> {item}
                </li>
              ))}
            </ul>
            <Link to="/proprietaires#contact-form" className="btn-primary w-full inline-flex items-center justify-center gap-2" data-testid="conciergerie-pricing-cta">
              Demander un devis <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Comparatif */}
      <section className="py-20 bg-[#1A120B]" data-testid="conciergerie-comparatif">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#C9922A] uppercase tracking-[0.2em] text-sm font-semibold mb-3 block">Comparatif</span>
            <h2 className="font-display text-3xl md:text-4xl text-white mb-3">Gérer seul vs. avec Happyhouse</h2>
            <p className="text-white/40 max-w-lg mx-auto text-sm">Voici ce que notre offre Partenariat change concrètement pour vous.</p>
          </div>
          <div className="bg-white/[0.04] rounded-2xl border border-white/10 overflow-hidden">
            <div className="grid grid-cols-3 bg-white/[0.06] px-6 py-4 border-b border-white/10">
              <span className="text-white/40 text-xs uppercase tracking-wider font-semibold">Prestation</span>
              <span className="text-white/40 text-xs uppercase tracking-wider font-semibold text-center">Gestion solo</span>
              <span className="text-[#C9922A] text-xs uppercase tracking-wider font-semibold text-center">Happyhouse</span>
            </div>
            {comparatif.map((row, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.04 }}
                className="grid grid-cols-3 px-6 py-4 border-b border-white/[0.06] last:border-0"
              >
                <span className="text-white/70 text-sm">{row.feature}</span>
                <span className="text-white/30 text-sm text-center">{row.solo}</span>
                <span className="text-[#C9922A]/80 text-sm text-center font-medium">{row.happyhouse}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-[#004333]" data-testid="conciergerie-stats">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: 98, suffix: "%", label: "Satisfaction", sub: "de nos voyageurs" },
              { value: 85, suffix: "%", label: "Taux d'occupation", sub: "moyen annuel" },
              { value: 25, suffix: "%", prefix: "+", label: "Revenus", sub: "vs location classique" },
              { value: 24, suffix: "h", prefix: "<", label: "Réactivité", sub: "en cas d'urgence" },
            ].map((stat, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <div className="text-3xl md:text-4xl font-black text-white mb-1">
                  <AnimatedCounter target={stat.value} suffix={stat.suffix} prefix={stat.prefix || ""} />
                </div>
                <p className="text-[#C9922A] font-semibold text-sm">{stat.label}</p>
                <p className="text-white/40 text-xs">{stat.sub}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Avantages */}
      <section className="py-20 bg-[#1A120B]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#C9922A] uppercase tracking-[0.2em] text-sm font-semibold mb-3 block">Avantages</span>
              <h2 className="font-display text-2xl md:text-3xl text-white mb-5">Pourquoi choisir le Partenariat ?</h2>
              <p className="text-white/60 leading-relaxed mb-8">
                L'offre Partenariat vous permet de bénéficier d'une gestion professionnelle tout en gardant la main sur votre bien.
                Vous percevez directement les revenus des plateformes, optimisés par notre expertise.
              </p>
              <Link to="/proprietaires#contact-form" className="inline-flex items-center gap-2 px-6 py-3 bg-[#C9922A] text-white rounded-full font-semibold hover:bg-[#B8831F] transition-colors" data-testid="conciergerie-cta">
                Devenir partenaire <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="bg-white/[0.05] rounded-2xl p-6 border border-white/10">
              <ul className="space-y-3">
                {avantages.map((point, i) => (
                  <motion.li key={i} initial={{ opacity: 0, x: 10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} className="flex items-start gap-3">
                    <Check className="w-4 h-4 text-[#C9922A] flex-shrink-0 mt-0.5" />
                    <span className="text-white/70 text-sm">{point}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Témoignages — Carrousel */}
      {(() => {
        const list = temoignages.length > 0 ? temoignages : [{
          id: "default", nom: "Marc D.", note: 5, type_temoignage: "proprietaire",
          texte: "Depuis que j'ai confié mon appartement à Happy House, mes revenus ont augmenté de 30% et je n'ai plus rien à gérer. L'équipe est réactive, professionnelle et transparente. Je recommande vivement.",
          logement_nom: "Limoges — Offre Partenariat"
        }];
        const tem = list[carouselIndex % list.length];
        const prev = () => { setCarouselIndex(i => (i - 1 + list.length) % list.length); setIsPaused(true); };
        const next = () => { setCarouselIndex(i => (i + 1) % list.length); setIsPaused(true); };

        if (typeof window !== "undefined") {
          clearTimeout(autoplayRef.current);
          if (!isPaused) {
            autoplayRef.current = setTimeout(() => {
              setCarouselIndex(i => (i + 1) % list.length);
            }, 5000);
          }
        }

        return (
          <section className="py-20 bg-[#2C1E14]" data-testid="conciergerie-testimonial">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <span className="text-[#C9922A] uppercase tracking-[0.2em] text-sm font-semibold mb-3 block">Témoignages</span>
                <h2 className="font-display text-3xl md:text-4xl text-white">Ils nous font confiance</h2>
              </div>

              <div
                onMouseEnter={() => setIsPaused(true)}
                onMouseLeave={() => setIsPaused(false)}
              >
                <motion.div
                  key={carouselIndex}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, ease: "easeOut" }}
                  className="relative bg-white/[0.05] border border-white/10 rounded-3xl overflow-hidden"
                >
                  {/* Barre de progression dorée */}
                  <div className="h-1 w-full bg-white/10">
                    <motion.div
                      className="h-full bg-[#C9922A]"
                      initial={{ width: "0%" }}
                      animate={{ width: isPaused ? undefined : "100%" }}
                      transition={{ duration: isPaused ? 0 : 5, ease: "linear" }}
                      key={`prog-${carouselIndex}-${isPaused}`}
                    />
                  </div>

                  <div className="p-8 md:p-12">
                    {/* Badge + étoiles */}
                    <div className="flex items-start justify-between mb-6">
                      <div className="flex gap-1.5">
                        {[1,2,3,4,5].map(n => (
                          <Star key={n} className={`w-5 h-5 ${n <= tem.note ? "fill-[#C9922A] text-[#C9922A]" : "text-white/10"}`} />
                        ))}
                      </div>
                      <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-[#C9922A]/20 text-[#C9922A] border border-[#C9922A]/30 uppercase tracking-wider">
                        Offre Partenariat
                      </span>
                    </div>

                    {/* Guillemet décoratif */}
                    <div className="absolute top-8 right-8 text-8xl text-[#C9922A]/10 font-serif leading-none select-none pointer-events-none">"</div>

                    {/* Texte */}
                    <blockquote className="text-white/90 text-lg md:text-xl leading-relaxed italic mb-8 relative z-10">
                      "{tem.texte}"
                    </blockquote>

                    {/* Auteur */}
                    <div className="flex items-center justify-between flex-wrap gap-4 pt-6 border-t border-white/10">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#C9922A] to-[#E8C874] flex items-center justify-center text-[#1A120B] font-bold text-lg flex-shrink-0">
                          {tem.nom.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="font-semibold text-white">{tem.nom}</div>
                          <div className="text-sm text-white/50">
                            {tem.logement_nom ? tem.logement_nom : "Propriétaire partenaire"}
                          </div>
                        </div>
                      </div>
                      {isPaused && <span className="text-xs text-white/30 italic">En pause</span>}
                    </div>
                  </div>
                </motion.div>

                {/* Navigation */}
                {list.length > 1 && (
                  <div className="flex items-center gap-5 mt-8">
                    <button onClick={prev}
                      className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/60 hover:text-white hover:border-white/50 hover:bg-white/10 transition-all flex-shrink-0">
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <div className="flex-1 flex gap-2">
                      {list.map((_, i) => (
                        <button key={i} onClick={() => { setCarouselIndex(i); setIsPaused(true); }}
                          className={`flex-1 h-1.5 rounded-full transition-all duration-300 ${
                            i === carouselIndex ? "bg-[#C9922A]" : i < carouselIndex ? "bg-white/40" : "bg-white/15"
                          }`}
                        />
                      ))}
                    </div>
                    <button onClick={next}
                      className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/60 hover:text-white hover:border-white/50 hover:bg-white/10 transition-all flex-shrink-0">
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                )}
                <p className="text-center text-xs text-white/30 mt-3 tracking-widest uppercase">
                  {(carouselIndex % list.length) + 1} / {list.length}
                </p>
              </div>
            </div>
          </section>
        );
      })()}

      {/* CTA */}
      <section className="py-16 bg-[#004333]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-display text-2xl md:text-3xl text-white mb-4">Prêt à confier votre bien ?</h2>
          <p className="text-white/60 mb-8 max-w-xl mx-auto">
            Contactez-nous pour un premier échange. Nous étudierons ensemble la meilleure stratégie pour votre bien.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#C9922A] text-white rounded-full font-semibold hover:bg-[#B8831F] transition-colors">
              Nous contacter <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/simulateur" className="inline-flex items-center gap-2 px-8 py-3.5 border-2 border-white/30 text-white rounded-full font-semibold hover:bg-white/10 transition-colors">
              Simuler mes revenus
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
